import classNames from "classnames";
import i18n from "i18next";
import { isEmpty } from "lodash";
import React from "react";
import {
  Control,
  useController,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import SVG from "react-inlinesvg";
import { useShallow } from "zustand/react/shallow";

import { useMobile } from "@krea/customer-features/hooks";
import info from "@krea/customer-features/images/svg/information-icon.svg";
import { settings } from "@krea/customer-features/settings";
import { AppFlowFormValues } from "@krea/customer-features/shared-components/application-form/hooks/useApplicationFlowForm";
import EmailInput from "@krea/customer-features/shared-components/loan-application-form/fields/email";
import OrganisationInput from "@krea/customer-features/shared-components/loan-application-form/fields/organisation-number";
import PhoneInput from "@krea/customer-features/shared-components/loan-application-form/fields/phone";
import LoanApplicationFormLayout from "@krea/customer-features/shared-components/loan-application-form-layout";
import { Text } from "@krea/customer-features/shared-components/text";
import { useHasOngoingApplication } from "@krea/customer-features/store/current-loan-application/hooks/useHasOngoingApplication";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import {
  COUNTRY_CODE,
  TEST_IDS,
  privacyPolicyLink,
  APPLICATION_NAME,
} from "@krea/customer-features/utils";

import CheckBox from "../../fields/checkbox";

const BasicDetailsForm = ({
  hasTestimonials,
  control,
  getValues,
  goToNextStep,
  setValue,
}: {
  hasTestimonials: boolean;
  control: Control<AppFlowFormValues>;
  getValues: UseFormGetValues<AppFlowFormValues>;
  currentStep: string;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  setValue: UseFormSetValue<AppFlowFormValues>;
}) => {
  const isMobile = useMobile();
  const { t } = useTranslation();

  // Zustand state
  const {
    organisation: { name: organisationName, organisationNumber },
    email,
    consentCheckbox,
    setOrganisation,
    setEmail,
    setMobilePhoneNumber,
    setConsentCheckbox,
    formErrors,
    removeFormError,
  } = useLoanApplicationFormStore(
    useShallow((state) => ({
      organisation: state.organisation,
      email: state.email,
      consentCheckbox: state.consentCheckbox,
      setOrganisation: state.setOrganisation,
      setEmail: state.setEmail,
      setMobilePhoneNumber: state.setMobilePhoneNumber,
      setConsentCheckbox: state.setConsentCheckbox,
      formErrors: state.formErrors,
      removeFormError: state.removeFormError,
    })),
  );

  const isPartnerWeb = settings.appName === APPLICATION_NAME.PARTNER_WEB;

  // react hook form state
  const {
    field: organisationNumberField,
    fieldState: organisationNumberFieldState,
  } = useController({
    name: "organisation.organisationNumber",
    control,
    defaultValue: "",
  });

  const { field: emailField, fieldState: emailFieldState } = useController({
    name: "email",
    control,
    defaultValue: "",
  });

  const { field: phoneNumberField, fieldState: phoneNumberFieldState } =
    useController({
      name: "mobilePhoneNumber",
      control,
      defaultValue: "",
    });

  const { field: consentCheckboxField, fieldState: consentCheckboxFieldState } =
    useController({
      name: "consentCheckbox",
      control,
      defaultValue: isPartnerWeb ? true : false,
    });

  // ===================

  const { data } = useHasOngoingApplication(
    settings.countryCode,
    email,
    organisationNumber,
  );
  const ongoingApplicationExists = data?.hasHit;

  let isLimitedCompany = false;

  if (organisationName && isEmpty(organisationNumberFieldState.error)) {
    const organisationNameToCheck = organisationName.toLowerCase() as string;

    switch (settings.countryCode) {
      case COUNTRY_CODE.SE:
        isLimitedCompany = organisationNameToCheck.endsWith(" ab");
        break;
      case COUNTRY_CODE.FI:
        isLimitedCompany = organisationNameToCheck.endsWith(" oy");
        break;
      default:
        isLimitedCompany = false;
    }
  }

  const innerForm = (
    <>
      <div className="tw-mt-2">
        <OrganisationInput
          value={organisationNumberField.value}
          error={organisationNumberFieldState.error?.message}
          touched={organisationNumberFieldState.isTouched}
          onCompanyChange={(company) => {
            setValue(
              "organisation.organisationNumber",
              company.organisationNumber,
              {
                shouldTouch: true,
                shouldDirty: true,
                shouldValidate: true,
              },
            );
            setValue("organisation.name", company.name, {
              shouldTouch: true,
              shouldDirty: true,
              shouldValidate: true,
            });
            setOrganisation(company);
          }}
          isLimitedCompany={isLimitedCompany}
          onBlur={organisationNumberField.onBlur}
        />
      </div>

      <div className="tw-mt-5">
        <EmailInput
          label={t("applicationForm.commons.emailLabel")}
          value={emailField.value}
          errors={emailFieldState.error?.message || formErrors.email}
          touched={formErrors.email || emailFieldState.isTouched}
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            emailField.onChange(e.target.value);
            setEmail(e.target.value);
            removeFormError("email");
          }}
          onBlur={emailField.onBlur}
        />
      </div>
      {ongoingApplicationExists && (
        <div
          className={classNames("tw-rounded tw-mt-3 tw-mb-5 tw-flex", {
            "tw-p-1": isMobile,
            "tw-py-2 tw-px-1": !isMobile,
          })}
          style={{ border: "1px solid #EBE8EF" }}
          data-test-id={
            TEST_IDS.common.forms.loanApplication.hasOngoingApplication
          }
        >
          <SVG
            src={info}
            style={
              isMobile
                ? { width: "22.5px", marginRight: "10px" }
                : { width: "20px" }
            }
            className="tw-ml-3 tw-h-auto"
          ></SVG>

          <div className={isMobile ? "tw-ml-1" : "tw-ml-3"}>
            <Text style={{ lineHeight: "1.5" }} size={isMobile ? "xs" : "sm"}>
              {t("applicationForm.common.applicationExists")}
            </Text>

            <Text size={isMobile ? "xs" : "sm"} className="tw-font-bold">
              <a href="/login#bankid-container">
                {t("applicationForm.common.loginToApplication")}
              </a>
            </Text>
          </div>
        </div>
      )}
      <div
        className={classNames("tw-mt-5", {
          "tw-mb-7": isPartnerWeb,
        })}
      >
        <PhoneInput
          label={t("applicationForm.commons.phoneLabel")}
          value={phoneNumberField.value}
          touched={phoneNumberFieldState.isTouched}
          errors={phoneNumberFieldState.error?.message}
          handleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setMobilePhoneNumber(e.target?.value);
            phoneNumberField.onChange(e.target?.value);
          }}
          onBlur={phoneNumberField.onBlur}
        />
      </div>

      {!isPartnerWeb ? (
        <CheckBox
          onChange={() => {
            setConsentCheckbox(!consentCheckbox);
            consentCheckboxField.onChange(!consentCheckboxField.value);
          }}
          checked={consentCheckboxField.value}
          error={consentCheckboxFieldState.error?.message}
          className="tw-my-7"
          label={
            <Trans
              i18nKey={"applicationForm.common.privacyPolicyConsent"}
              values={{
                company: (() => {
                  switch (settings.countryCode) {
                    case COUNTRY_CODE.FI:
                      return "Kredita";
                    case COUNTRY_CODE.SE:
                      return "Krea";
                    default:
                      return "";
                  }
                })(),
              }}
              components={{
                1: (
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  <a
                    target="_blank"
                    href={privacyPolicyLink(
                      settings.countryCode,
                      settings.countryWebsiteUrl,
                      i18n.language,
                    )}
                    rel="noopener noreferrer"
                    className="tw-font-bold tw-text-primary tw-no-underline"
                  />
                ),
              }}
            />
          }
          labelClassName="tw-font-normal tw-text-sm"
        />
      ) : null}
    </>
  );

  const infoText: React.ReactNode = (
    <span>
      {t("applicationForm.common.businessNotFoundContactSupport")}
      {isMobile && <br />}
      {t("applicationForm.common.callUs")}{" "}
      <a
        href={`tel:${settings.contacts.phone.link}`}
        target={"_self"}
        className="tw-font-bold tw-text-primary tw-whitespace-nowrap"
      >
        {settings.contacts.phone.value}
      </a>
    </span>
  );

  return (
    <LoanApplicationFormLayout
      heading={t("applicationForm.common.startApplicationHeading")}
      subheading={t("applicationForm.common.startApplicationSubheading")}
      innerForm={innerForm}
      hideAdvisorCTABanner={true}
      bottomInfoText={infoText}
      hasTestimonials={hasTestimonials}
      goToNextStep={goToNextStep}
      goToPreviousStep={() => {}}
      disabledNavigationBeforeFormCompletion={
        !(
          organisationNumberField.value &&
          emailField.value &&
          phoneNumberField.value &&
          consentCheckboxField.value === true &&
          isEmpty(organisationNumberFieldState.error) &&
          isEmpty(emailFieldState.error) &&
          isEmpty(phoneNumberFieldState.error)
        )
      }
      borderlessInnerForm={true}
    />
  );
};

export default BasicDetailsForm;
